<script lang="ts" setup>
import { PullRefresh } from "vant";
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";
import { useScroll } from "@vueuse/core";
import ScrollLoadingList, {
  ScrollLoadingListInstance,
} from "@/components/mobile/ScrollLoadingList.vue";
import { useGlobalUser } from "@/stores/globalUser";
import { onActivated } from "vue";

/**
 * 支持下拉加载和滚动加载的容器
 */
interface ThreadList {
  onPullLoad?: () => Promise<any>;
  onScrollLoad?: () => Promise<boolean>;
  enablePullLoad?: boolean;
  enableScrollLoad?: boolean;
  scroller?: HTMLElement | null;
  showBottomTip?: boolean;
  immediateCheck?: boolean;
  enableBackTop?: boolean;
  needLogin?: boolean;
  enableScrollRecord?: boolean;
}

const props = withDefaults(defineProps<ThreadList>(), {
  onPullLoad: async () => {},
  onScrollLoad: async () => false,
  enablePullLoad: true,
  enableScrollLoad: true,
  showBottomTip: true,
  immediateCheck: true,
  enableBackTop: true,
  needLogin: false,
  enableScrollRecord: false,
});
const emit = defineEmits<{
  (e: "onScroll", scrollTop: number): void;
}>();

const listId = `list-${getCurrentInstance()?.uid}`;
onMounted(() => {
  listRef.value?.classList.add(listId);
});

// 下拉加载
const userStore = useGlobalUser();
const scrollLoadingListRef = ref<ScrollLoadingListInstance | null>(null);
const loading = ref(false);
const onPullRefresh = async () => {
  loading.value = true;
  try {
    await props.onPullLoad();
  } finally {
    loading.value = false;
  }
};

const listRef = ref<HTMLElement | null>(null);
const scrollRef = useScroll(listRef, {
  behavior: "smooth",
  onScroll: (e) => {
    emit("onScroll", (e.target as HTMLElement).scrollTop);
  },
});
const scrollTo = (y: number) => {
  listRef.value!.scrollTo({ top: y, behavior: "instant" });
};
const scrollToTop = () => {
  const currentY = scrollRef.y.value;
  scrollRef.y.value = 0;
  if (currentY === 0) {
    onPullRefresh();
  }
};
const currentScroller = computed(() => props.scroller || listRef.value);

const useScrollRecord = () => {
  const actualScrollRef = computed<HTMLElement | null>(() => {
    if (props.enableScrollRecord) {
      return props.scroller || listRef.value;
    }
    return null;
  });

  const { y } = useScroll(actualScrollRef);
  const lastScrollY = ref(0);
  watch(y, (newY) => {
    if (props.enableScrollRecord) {
      lastScrollY.value = newY;
    }
  });

  onActivated(() => {
    if (props.enableScrollRecord) {
      y.value = lastScrollY.value;
    }
  });
};

if (props.enableScrollRecord) {
  useScrollRecord();
}

export interface ThreadListInstance {
  scrollTo: typeof scrollTo;
  scrollToTop: typeof scrollToTop;
  onPullRefresh: () => Promise<void>;
  resetScrollLoadingState: () => void;
}

defineExpose({
  scrollTo,
  scrollToTop,
  onPullRefresh,
  resetScrollLoadingState: () => {
    scrollLoadingListRef.value?.resetScrollLoadingState();
  },
});
</script>
<template>
  <div ref="listRef" class="h-full overflow-auto">
    <login-request-tip
      v-if="needLogin && !userStore.isLogin"
      :title="$t('Empty.loginFirst.moments')"
    />
    <pull-refresh
      v-else
      v-model="loading"
      :disabled="!props.enablePullLoad"
      class="w-full"
      @refresh="onPullRefresh"
    >
      <template #pulling>
        <div class="w-full text-center">{{ $t("Message.pullRefresh") }}</div>
      </template>
      <template #loosing>
        <div class="w-full text-center">{{ $t("Message.releaseRefresh") }}</div>
      </template>
      <template #loading>
        <div class="w-full h-full flex justify-center items-center">
          <span class="loading loading-ring"></span>
        </div>
      </template>

      <template #default>
        <scroll-loading-list
          ref="scrollLoadingListRef"
          :enable-scroll-load="enableScrollLoad"
          :immediateCheck="immediateCheck"
          :on-scroll-load="onScrollLoad"
          :scroller="scroller"
          :showBottomTip="props.showBottomTip"
        >
          <slot></slot>
          <VanBackTop
            v-if="currentScroller && enableBackTop"
            :target="`.${listId}`"
            :teleport="`.${listId}`"
            bottom="12vh"
            class="bg-primary sticky float-right right-5"
          />
        </scroll-loading-list>
      </template>
    </pull-refresh>
  </div>
</template>
